/**
 * KpiHeader — the 4 KPI cards (Story UI-U7, AC 3).
 *
 * Jahresprämie · Courtage YTD (fakturiert) · aktive Policen · Kunden. Money/counts in
 * the UI-U1 mono-num treatment (Geist Mono + tabular figures). Deltas render ONLY when
 * non-null: Jahresprämie = % vs 12 months ago (soft Tag, ok/alert tone by sign),
 * Policen/Kunden = +N (30 Tage). A gated/absent money value renders `—`, never CHF 0.
 * Cards are neutral chrome (color = signal): no colour except the delta tag.
 *
 * [Source: brokly-design-reference/components/brokly/heute-view.tsx (KPI cards)]
 * [Source: docs/stories/ui-u7-portfolio.md AC 3]
 */

import type { PortfolioKpisDto } from '@brokly/shared';
import { Card } from '../../../components/ui/Card';
import { Badge } from '../../../components/ui/Badge';
import { chfOrDash } from '../util';

function KpiCard({
  label,
  value,
  delta,
}: {
  label: string;
  value: string;
  delta?: React.ReactNode;
}) {
  return (
    <Card className="rounded-xl p-4">
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className="mt-1.5 flex items-baseline gap-2">
        <span className="mono-num text-2xl font-semibold text-foreground">{value}</span>
        {delta}
      </div>
    </Card>
  );
}

export function KpiHeader({ kpis }: { kpis: PortfolioKpisDto }) {
  const praemieDelta =
    kpis.jahrespraemie_delta_pct !== null ? (
      <Badge variant={kpis.jahrespraemie_delta_pct >= 0 ? 'success' : 'danger'}>
        {kpis.jahrespraemie_delta_pct >= 0 ? '+' : ''}
        {kpis.jahrespraemie_delta_pct.toFixed(1)}%
      </Badge>
    ) : undefined;

  const policenDelta =
    kpis.aktive_policen_delta_30d > 0 ? (
      <Badge variant="neutral">+{kpis.aktive_policen_delta_30d} (30 Tage)</Badge>
    ) : undefined;

  const kundenDelta =
    kpis.kunden_delta_30d > 0 ? (
      <Badge variant="neutral">+{kpis.kunden_delta_30d} (30 Tage)</Badge>
    ) : undefined;

  const versichererDelta =
    kpis.versicherer_delta_30d > 0 ? (
      <Badge variant="neutral">+{kpis.versicherer_delta_30d} (30 Tage)</Badge>
    ) : undefined;

  return (
    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
      <KpiCard label="Jahresprämie" value={chfOrDash(kpis.jahrespraemie_chf)} delta={praemieDelta} />
      <KpiCard label="Courtage YTD (fakturiert)" value={chfOrDash(kpis.courtage_ytd_chf)} />
      <KpiCard
        label="Aktive Policen"
        value={String(kpis.aktive_policen)}
        delta={policenDelta}
      />
      <KpiCard label="Kunden" value={String(kpis.kunden)} delta={kundenDelta} />
      <KpiCard label="Versicherer" value={String(kpis.versicherer)} delta={versichererDelta} />
    </div>
  );
}
